import { useState, useMemo } from 'react'
import { NavBar, Popup, Tag, Button } from 'react-vant'
import { ArrowLeft, InfoO, Cross, ArrowDown, ArrowUp, ShieldO } from '@react-vant/icons'
import { useNavigate } from 'react-router-dom'
import {
    STANDARDS_METADATA,
    GRADE_STAGES,
    ITEM_CONFIG,
    ITEM_WEIGHTS,
    STANDARDS_DATA,
} from '../../constants/nationalStandardsData'

export default function NationalStandards() {
    const navigate = useNavigate()

    // 筛选状态
    const [selectedStageKey, setSelectedStageKey] = useState('primary')
    const [selectedGrade, setSelectedGrade] = useState('小学一年级')
    const [selectedGender, setSelectedGender] = useState('男') // '男' | '女'
    const [expandedItemCode, setExpandedItemCode] = useState(null)
    const [showRuleModal, setShowRuleModal] = useState(false)
    const [ruleTab, setRuleTab] = useState('exam') // 'exam' (赋分总则) | 'spec' (操作与防犯规)

    // 当前学段
    const currentStage = useMemo(() => {
        return GRADE_STAGES.find(s => s.key === selectedStageKey) || GRADE_STAGES[0]
    }, [selectedStageKey])

    // 切换学段时同步切换到该学段第一个年级
    const handleStageChange = (stage) => {
        setSelectedStageKey(stage.key)
        if (stage.grades && stage.grades.length > 0) {
            setSelectedGrade(stage.grades[0])
        }
    }

    // 获取当前年级和性别对应的所有项目标准
    const currentItems = useMemo(() => {
        const items = []
        let weightMap = ITEM_WEIGHTS[selectedGrade]
        if (!weightMap) {
            weightMap = selectedGender === '男' ? ITEM_WEIGHTS['default_male'] : ITEM_WEIGHTS['default_female']
        }

        Object.keys(ITEM_CONFIG).forEach(code => {
            const key = `${selectedGrade}_${selectedGender}_${code}`
            const std = STANDARDS_DATA[key]
            if (std && std.scoring_table) {
                const config = ITEM_CONFIG[code] || {}
                const weight = weightMap?.[code] || std.weight || 0
                items.push({
                    item_code: code,
                    item_name: config.label || std.item_name,
                    unit: config.unit || '',
                    desc: config.desc || '',
                    weight: weight,
                    scoring_table: std.scoring_table,
                })
            }
        })

        return items.sort((a, b) => (b.weight || 0) - (a.weight || 0))
    }, [selectedGrade, selectedGender])

    // 解析项目的优秀/良好/及格/不及格区间展示
    const getTierSummary = (item) => {
        const table = item.scoring_table || {}
        if (item.item_code === 'bmi') {
            return {
                excellent: table['100'] ? `${table['100']}` : '标准正常',
                good: table['80'] ? `${table['80']}` : '超重/低体重',
                pass: table['60'] ? `${table['60']}` : '肥胖',
                fail: '--',
            }
        }

        const isTimeItem = ['run_50m', 'run_800m', 'run_1000m', 'run_50m8x'].includes(item.item_code)

        const v90 = table['90']
        const v80 = table['80']
        const v60 = table['60']

        if (isTimeItem) {
            return {
                excellent: v90 ? `≤ ${v90} ${item.unit}` : `--`,
                good: v80 && v90 ? `${v90} ~ ${v80} ${item.unit}` : `--`,
                pass: v60 && v80 ? `${v80} ~ ${v60} ${item.unit}` : `--`,
                fail: v60 ? `> ${v60} ${item.unit}` : `--`,
            }
        }

        return {
            excellent: v90 ? `≥ ${v90} ${item.unit}` : `--`,
            good: v80 && v90 ? `${v80} ~ ${v90} ${item.unit}` : `--`,
            pass: v60 && v80 ? `${v60} ~ ${v80} ${item.unit}` : `--`,
            fail: v60 ? `< ${v60} ${item.unit}` : `--`,
        }
    }

    const toggleExpand = (itemCode) => {
        setExpandedItemCode(prev => prev === itemCode ? null : itemCode)
    }

    return (
        <div style={{ background: 'var(--canvas-bg, #f8fafc)', minHeight: '100vh', paddingBottom: 50 }}>
            {/* ========== 顶部导航 ========== */}
            <NavBar
                title="2014版体测评分标准"
                leftArrow={<ArrowLeft />}
                onClickLeft={() => navigate(-1)}
                rightText={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--primary-color, #056b4f)', fontSize: 13, fontWeight: 600 }}>
                        <InfoO style={{ fontSize: 16 }} />
                        <span>评分规则</span>
                    </span>
                }
                onClickRight={() => setShowRuleModal(true)}
                fixed
                placeholder
            />

            {/* ========== 顶部分段选择控制栏 ========== */}
            <div style={{
                background: '#fff',
                borderBottom: '1px solid var(--border-subtle, #e2e8f0)',
                padding: '12px 16px',
                position: 'sticky',
                top: 46,
                zIndex: 90
            }}>
                {/* 1. 学段选择器 */}
                <div style={{
                    display: 'flex',
                    background: '#f1f5f9',
                    borderRadius: 12,
                    padding: 3,
                    marginBottom: 12
                }}>
                    {GRADE_STAGES.map(stage => {
                        const active = stage.key === selectedStageKey
                        return (
                            <div
                                key={stage.key}
                                onClick={() => handleStageChange(stage)}
                                style={{
                                    flex: 1,
                                    textAlign: 'center',
                                    padding: '7px 0',
                                    borderRadius: 10,
                                    fontSize: 13,
                                    fontWeight: active ? 700 : 500,
                                    color: active ? 'var(--primary-color, #056b4f)' : 'var(--text-secondary, #475569)',
                                    background: active ? '#ffffff' : 'transparent',
                                    boxShadow: active ? '0 1px 4px rgba(0,0,0,0.06)' : 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease'
                                }}
                            >
                                {stage.label}
                            </div>
                        )
                    })}
                </div>

                {/* 2. 年级横向滑动 + 性别切换 */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    {/* 年级药丸横向滑动 */}
                    <div style={{
                        display: 'flex',
                        overflowX: 'auto',
                        gap: 8,
                        flex: 1,
                        paddingBottom: 2,
                        scrollbarWidth: 'none'
                    }}>
                        {currentStage.grades.map(grade => {
                            const active = grade === selectedGrade
                            const shortLabel = grade.replace(/^(小学|初中|高中|大学)/, '')
                            return (
                                <div
                                    key={grade}
                                    onClick={() => setSelectedGrade(grade)}
                                    style={{
                                        flexShrink: 0,
                                        padding: '5px 12px',
                                        borderRadius: 20,
                                        fontSize: 12,
                                        fontWeight: active ? 700 : 500,
                                        background: active ? '#f0fdf4' : '#f8fafc',
                                        color: active ? 'var(--primary-color, #056b4f)' : 'var(--text-secondary, #475569)',
                                        border: active ? '1px solid #a7f3d0' : '1px solid #e2e8f0',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {shortLabel}
                                </div>
                            )
                        })}
                    </div>

                    {/* 性别选择器 */}
                    <div style={{
                        display: 'flex',
                        background: '#f1f5f9',
                        borderRadius: 12,
                        padding: 2,
                        flexShrink: 0
                    }}>
                        <div
                            onClick={() => setSelectedGender('男')}
                            style={{
                                padding: '4px 10px',
                                borderRadius: 10,
                                fontSize: 12,
                                fontWeight: selectedGender === '男' ? 700 : 500,
                                color: selectedGender === '男' ? '#fff' : '#64748b',
                                background: selectedGender === '男' ? 'var(--primary-color, #056b4f)' : 'transparent',
                                cursor: 'pointer'
                            }}
                        >
                            男生
                        </div>
                        <div
                            onClick={() => setSelectedGender('女')}
                            style={{
                                padding: '4px 10px',
                                borderRadius: 10,
                                fontSize: 12,
                                fontWeight: selectedGender === '女' ? 700 : 500,
                                color: selectedGender === '女' ? '#fff' : '#64748b',
                                background: selectedGender === '女' ? '#ec4899' : 'transparent',
                                cursor: 'pointer'
                            }}
                        >
                            女生
                        </div>
                    </div>
                </div>
            </div>

            {/* ========== 页面内容区域 ========== */}
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* 1. 年级项目权重与概览卡片 */}
                <div style={{
                    background: 'var(--card-bg, #ffffff)',
                    borderRadius: 16,
                    padding: '16px',
                    border: '1px solid var(--border-subtle, #e2e8f0)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            background: '#f0fdf4', padding: '3px 10px', borderRadius: 10,
                            fontSize: 12, fontWeight: 700, color: 'var(--primary-color, #056b4f)'
                        }}>
                            <span>🎖️</span>
                            <span>{selectedGrade} · {selectedGender}生测试组合</span>
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary, #94a3b8)' }}>
                            权重总计 100%
                        </span>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                        {currentItems.map(item => (
                            <div
                                key={item.item_code}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 4,
                                    background: '#f8fafc', border: '1px solid #f1f5f9',
                                    borderRadius: 8, padding: '4px 8px', fontSize: 11
                                }}
                            >
                                <span style={{ color: 'var(--text-secondary, #475569)' }}>{item.item_name}</span>
                                <span style={{ fontWeight: 700, color: 'var(--primary-color, #056b4f)' }}>{item.weight}%</span>
                            </div>
                        ))}
                    </div>

                    <div style={{ fontSize: 11, color: 'var(--text-tertiary, #94a3b8)', lineHeight: 1.5 }}>
                        标准分满分 100 分。学年总评成绩达 80.0 分以上者方可具备评优评奖资格。
                    </div>
                </div>

                {/* 2. 测验项目详细评分标准卡片列表 */}
                <div>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        marginBottom: 12, paddingLeft: 4
                    }}>
                        <span style={{ width: 4, height: 15, background: 'var(--primary-color, #056b4f)', borderRadius: 2 }} />
                        <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>
                            项目评分标准对照表
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--text-tertiary, #94a3b8)' }}>
                            包含 {currentItems.length} 项必测项目四级标准阈值
                        </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {currentItems.map(item => {
                            const tier = getTierSummary(item)
                            const isExpanded = expandedItemCode === item.item_code
                            const st = item.scoring_table || {}
                            const scoreKeys = Object.keys(st).sort((a, b) => Number(b) - Number(a))

                            return (
                                <div
                                    key={item.item_code}
                                    style={{
                                        background: 'var(--card-bg, #ffffff)',
                                        borderRadius: 16,
                                        padding: '16px',
                                        border: '1px solid var(--border-subtle, #e2e8f0)',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                                    }}
                                >
                                    {/* 卡片头部 */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary, #0f172a)' }}>
                                                    {item.item_name}
                                                </span>
                                                {item.weight > 0 && (
                                                    <span style={{
                                                        background: '#f0fdf4', color: 'var(--primary-color, #056b4f)',
                                                        fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 6
                                                    }}>
                                                        权重 {item.weight}%
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: 11, color: 'var(--text-tertiary, #94a3b8)', marginTop: 4 }}>
                                                {item.desc} (单位: {item.unit})
                                            </div>
                                        </div>
                                    </div>

                                    {/* 4级标准速查带 (优秀 / 良好 / 及格 / 不及格) */}
                                    <div style={{
                                        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6,
                                        marginBottom: 12
                                    }}>
                                        {/* 优秀 */}
                                        <div style={{
                                            background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 10,
                                            padding: '8px 4px', textAlign: 'center'
                                        }}>
                                            <div style={{ fontSize: 10, fontWeight: 700, color: '#059669' }}>优秀(90~100)</div>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#047857', marginTop: 4 }}>
                                                {tier.excellent}
                                            </div>
                                        </div>

                                        {/* 良好 */}
                                        <div style={{
                                            background: '#ECFEFF', border: '1px solid #A5F3FC', borderRadius: 10,
                                            padding: '8px 4px', textAlign: 'center'
                                        }}>
                                            <div style={{ fontSize: 10, fontWeight: 700, color: '#0891b2' }}>良好(80~85)</div>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#0e7490', marginTop: 4 }}>
                                                {tier.good}
                                            </div>
                                        </div>

                                        {/* 及格 */}
                                        <div style={{
                                            background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10,
                                            padding: '8px 4px', textAlign: 'center'
                                        }}>
                                            <div style={{ fontSize: 10, fontWeight: 700, color: '#d97706' }}>及格(60~78)</div>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#b45309', marginTop: 4 }}>
                                                {tier.pass}
                                            </div>
                                        </div>

                                        {/* 不及格 */}
                                        <div style={{
                                            background: '#FFF1F2', border: '1px solid #FECDD3', borderRadius: 10,
                                            padding: '8px 4px', textAlign: 'center'
                                        }}>
                                            <div style={{ fontSize: 10, fontWeight: 700, color: '#e11d48' }}>不及格(&lt;60)</div>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#be123c', marginTop: 4 }}>
                                                {tier.fail}
                                            </div>
                                        </div>
                                    </div>

                                    {/* 展开完整梯度表开关 */}
                                    <div
                                        onClick={() => toggleExpand(item.item_code)}
                                        style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                                            padding: '8px 0', borderTop: '1px dashed #f1f5f9',
                                            color: 'var(--primary-color, #056b4f)', fontSize: 12, fontWeight: 600,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <span>{isExpanded ? '收起梯级分值明细表' : '展开查看 10~100 分逐级对照表'}</span>
                                        {isExpanded ? <ArrowUp style={{ fontSize: 12 }} /> : <ArrowDown style={{ fontSize: 12 }} />}
                                    </div>

                                    {/* 完整梯级分值表格 */}
                                    {isExpanded && (
                                        <div style={{
                                            marginTop: 10, background: '#f8fafc', borderRadius: 12,
                                            padding: '8px 12px', border: '1px solid #f1f5f9'
                                        }}>
                                            <div style={{
                                                display: 'grid', gridTemplateColumns: '1.2fr 1.5fr 2fr',
                                                padding: '6px 0', borderBottom: '1px solid #e2e8f0',
                                                fontSize: 11, fontWeight: 700, color: '#64748b'
                                            }}>
                                                <span>得分</span>
                                                <span>评定等级</span>
                                                <span style={{ textAlign: 'right' }}>标准指标值 ({item.unit})</span>
                                            </div>
                                            {scoreKeys.map(score => {
                                                const numScore = Number(score)
                                                let level = '不及格'
                                                let levelColor = '#e11d48'
                                                if (numScore >= 90) {
                                                    level = '优秀'
                                                    levelColor = '#fa8c16'
                                                } else if (numScore >= 80) {
                                                    level = '良好'
                                                    levelColor = '#056b4f'
                                                } else if (numScore >= 60) {
                                                    level = '及格'
                                                    levelColor = '#00b578'
                                                }

                                                return (
                                                    <div
                                                        key={score}
                                                        style={{
                                                            display: 'grid', gridTemplateColumns: '1.2fr 1.5fr 2fr',
                                                            padding: '7px 0', borderBottom: '1px solid #f1f5f9',
                                                            fontSize: 12, alignItems: 'center'
                                                        }}
                                                    >
                                                        <span style={{ fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>
                                                            {score} 分
                                                        </span>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                            <span style={{
                                                                width: 6, height: 6, borderRadius: '50%',
                                                                background: levelColor
                                                            }} />
                                                            <span style={{ color: levelColor, fontWeight: 600 }}>
                                                                {level}
                                                            </span>
                                                        </div>
                                                        <span style={{ textAlign: 'right', fontWeight: 600, color: '#334155' }}>
                                                            {st[score]}
                                                        </span>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>

                {/* 3. 底部权威声明 */}
                <div style={{
                    background: '#f8fafc',
                    borderRadius: 14,
                    padding: '14px 16px',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10
                }}>
                    <ShieldO style={{ fontSize: 18, color: '#00b578', marginTop: 2, flexShrink: 0 }} />
                    <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>
                            权威标准保障与法律效力
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary, #94a3b8)', marginTop: 4, lineHeight: 1.5 }}>
                            本标准严格依据教育部《国家学生体质健康标准（2014年修订）》教体艺〔2014〕4号文件编制。数据离线内嵌于系统，保障现场极速查验与合规换算。
                        </div>
                    </div>
                </div>
            </div>

            {/* ========== 评分规则与政策解读弹窗 (Rule Modal) ========== */}
            <Popup
                visible={showRuleModal}
                position="bottom"
                round
                style={{ height: '85%', maxHeight: '85%' }}
                onClose={() => setShowRuleModal(false)}
            >
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* 弹窗头部 */}
                    <div style={{
                        padding: '16px 20px', borderBottom: '1px solid #f1f5f9',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                        <div>
                            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary, #0f172a)' }}>
                                国家体测与赋分规则
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text-tertiary, #94a3b8)', marginTop: 2 }}>
                                教育部标准 · 过程考核与三类六档政策
                            </div>
                        </div>
                        <div
                            onClick={() => setShowRuleModal(false)}
                            style={{ padding: 4, cursor: 'pointer', color: '#94a3b8' }}
                        >
                            <Cross style={{ fontSize: 18 }} />
                        </div>
                    </div>

                    {/* 分段标签切换栏 */}
                    <div style={{ display: 'flex', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <div
                            onClick={() => setRuleTab('exam')}
                            style={{
                                flex: 1, padding: '12px 0', textAlign: 'center', fontSize: 13,
                                fontWeight: ruleTab === 'exam' ? 700 : 500,
                                color: ruleTab === 'exam' ? 'var(--primary-color, #056b4f)' : '#64748b',
                                borderBottom: ruleTab === 'exam' ? '2.5px solid var(--primary-color, #056b4f)' : '2.5px solid transparent',
                                cursor: 'pointer'
                            }}
                        >
                            中考赋分与国标总则
                        </div>
                        <div
                            onClick={() => setRuleTab('spec')}
                            style={{
                                flex: 1, padding: '12px 0', textAlign: 'center', fontSize: 13,
                                fontWeight: ruleTab === 'spec' ? 700 : 500,
                                color: ruleTab === 'spec' ? 'var(--primary-color, #056b4f)' : '#64748b',
                                borderBottom: ruleTab === 'spec' ? '2.5px solid var(--primary-color, #056b4f)' : '2.5px solid transparent',
                                cursor: 'pointer'
                            }}
                        >
                            项目操作与防犯规
                        </div>
                    </div>

                    {/* 弹窗内容滚动区 */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {ruleTab === 'exam' ? (
                            <>
                                {/* 80分即满分官方横幅 */}
                                <div style={{
                                    background: 'linear-gradient(135deg, #f0fdf4 0%, #e8f6f0 100%)',
                                    borderRadius: 14, padding: '14px 16px', border: '1px solid #a7f3d0'
                                }}>
                                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--primary-color, #056b4f)', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span>✨</span>
                                        <span>官方核心导向：80分即满分！</span>
                                    </div>
                                    <div style={{ fontSize: 12, color: '#04523c', marginTop: 4, lineHeight: 1.5 }}>
                                        体测为合格性评价，非淘汰选拔。实测得分达到 80.0 分及以上（即良好或优秀），当次过程考核即按满分 10 分直接计入中考！
                                    </div>
                                </div>

                                {/* 三类六档对照表 */}
                                <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                    <div style={{
                                        display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1fr 1fr',
                                        background: '#f8fafc', padding: '10px 12px', fontSize: 11,
                                        fontWeight: 700, color: '#64748b'
                                    }}>
                                        <span>等级档次</span>
                                        <span>体测实测分</span>
                                        <span>单次赋分</span>
                                        <span>总分(40分)</span>
                                    </div>
                                    {[
                                        { level: '良好及以上', range: '≥ 80.0 分', single: '10分(满分)', total: '40分', color: 'var(--primary-color, #056b4f)', bold: true },
                                        { level: '及格 (一档)', range: '75.0 ~ 79.9', single: '8.5分', total: '34分' },
                                        { level: '及格 (二档)', range: '70.0 ~ 74.9', single: '8.0分', total: '32分' },
                                        { level: '及格 (三档)', range: '65.0 ~ 69.9', single: '7.5分', total: '30分' },
                                        { level: '及格 (四档)', range: '60.0 ~ 64.9', single: '7.0分', total: '28分' },
                                        { level: '不及格', range: '0 ~ 59.9', single: '5.5分', total: '22分', color: '#e11d48' },
                                    ].map((row, idx) => (
                                        <div
                                            key={idx}
                                            style={{
                                                display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1fr 1fr',
                                                padding: '10px 12px', borderTop: '1px solid #f1f5f9',
                                                fontSize: 12, alignItems: 'center',
                                                color: row.color || 'var(--text-primary, #0f172a)',
                                                fontWeight: row.bold ? 700 : 500
                                            }}
                                        >
                                            <span>{row.level}</span>
                                            <span>{row.range}</span>
                                            <span style={{ fontWeight: 700 }}>{row.single}</span>
                                            <span>{row.total}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* 国标总则 */}
                                <div style={{ background: '#fff', borderRadius: 14, padding: '16px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary, #0f172a)', marginBottom: 8 }}>
                                        二、国家标准分值构成与评优门槛
                                    </div>
                                    <div style={{ fontSize: 12, color: 'var(--text-secondary, #475569)', lineHeight: 1.6 }}>
                                        1. <strong>学年总分计算</strong>：总分由标准分（100分）与附加分（20分）组成，满分为 120 分。标准分由各单项得分与权重乘积之和构成。<br />
                                        2. <strong>等级标准</strong>：优秀（≥90.0 分）、良好（80.0～89.9 分）、及格（60.0～79.9 分）、不及格（≤59.9 分）。<br />
                                        3. <strong>评优门槛</strong>：学年体测成绩评定达到【良好（80.0分）及以上】者，方具备三好学生、奖学金等评优资格。毕业时总评成绩不足 50 分者按结业或肄业处理。
                                    </div>
                                </div>

                                {/* 附加分规则 */}
                                <div style={{ background: '#fff', borderRadius: 14, padding: '16px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary, #0f172a)', marginBottom: 8 }}>
                                        三、附加分加分机制（最高可加 20 分）
                                    </div>
                                    <div style={{ fontSize: 12, color: 'var(--text-secondary, #475569)', lineHeight: 1.6 }}>
                                        1. <strong>小学阶段（上限 20 分）</strong>：加分指标为【1分钟跳绳】。在达到满分标准基础上，每多跳 2 个加 1 分，最高累加 20 分。<br />
                                        2. <strong>初中/高中阶段（上限 20 分）</strong>：男生加分项为【引体向上】（最高10分）+【1000米跑】（最高10分）；女生加分项为【1分钟仰卧起坐】（最高10分）+【800米跑】（最高10分）。
                                    </div>
                                </div>

                                {/* 免考与缓测 */}
                                <div style={{ background: '#fff', borderRadius: 14, padding: '16px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary, #0f172a)', marginBottom: 8 }}>
                                        四、免考缓测与统测答疑
                                    </div>
                                    <div style={{ fontSize: 12, color: 'var(--text-secondary, #475569)', lineHeight: 1.6 }}>
                                        1. <strong>残疾学生免考</strong>：持《残疾人证》办理手续存入档案，体测统测直接按【满分（10分）】记录计入中考！<br />
                                        2. <strong>伤病缓测</strong>：因伤病无法如期测试，须在各区规定时间内申请缓测手续，按缓测成绩赋分；未办理手续者按 0 分计。
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Tab 2: 常见项目操作规范与防犯规 */}
                                {[
                                    {
                                        title: '1. 身高 / 体重测试',
                                        badge: '形态',
                                        action: '赤足、立正姿势站立于台上，足跟并拢、足尖分开约60度，躯干自然垂直。',
                                        errors: '穿鞋站立；发辫发结未解开；身体过度前倾或后仰。'
                                    },
                                    {
                                        title: '2. 一分钟跳绳测试',
                                        badge: '重点加分项',
                                        action: '双脚并跳，两手握绳柄，两臂屈肘将小臂抬平、绳拉直为适宜长度，听口令启动。',
                                        errors: '单脚连续跳、向后反向跳（均不计数）。跳绳绊脚该次不计，但表不停，立即继续跳！'
                                    },
                                    {
                                        title: '3. 坐位体前屈测试',
                                        badge: '柔韧',
                                        action: '赤足平坐于垫上，双腿伸直脚跟贴挡板，双臂伸直匀速前推，双手不得中途停顿或冲击。',
                                        errors: '膝关节弯曲、单手向前推板、推板突然发力冲撞。'
                                    },
                                    {
                                        title: '4. 50米跑测试',
                                        badge: '速度',
                                        action: '站立式起跑，听到“跑”口令后全力冲刺，以躯干冲过终点线垂直面为准。',
                                        errors: '抢跑犯规；串道阻碍他人冲刺。穿运动鞋或胶鞋，严禁穿钉鞋。'
                                    },
                                    {
                                        title: '5. 肺活量测试',
                                        badge: '机能',
                                        action: '握紧吹嘴密封，尽力深吸气后对准吹口匀速深呼气，直到不能再呼为止，测试 2 次取最大值。',
                                        errors: '两次吸气、漏气、呼气太急导致气流堵塞。'
                                    },
                                ].map((item, idx) => (
                                    <div key={idx} style={{ background: '#fff', borderRadius: 14, padding: '16px', border: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                            <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>{item.title}</span>
                                            <Tag type="primary" size="mini">{item.badge}</Tag>
                                        </div>
                                        <div style={{ fontSize: 12, color: 'var(--text-secondary, #475569)', lineHeight: 1.6 }}>
                                            • <strong>标准动作</strong>：{item.action}<br />
                                            • <strong>防犯规提示</strong>：{item.errors}
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                </div>
            </Popup>
        </div>
    )
}

